import React, { useEffect, useState } from 'react';
import { Divider, Form, Button } from 'semantic';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';

import CheckboxField from 'components/form-fields/formik/CheckboxField';
import Countries, {
  CountryOption,
} from 'components/form-fields/formik/Countries';
import InputField from 'components/form-fields/formik/InputField';
import PhoneCountry from 'components/form-fields/formik/PhoneCountry';
import PhoneNumber from 'components/form-fields/formik/PhoneNumber';
import LegalLink from 'components/LegalLink';
import RequiredIndicator from 'components/RequiredIndicator';
import ErrorMessage from 'components/ErrorMessage';

import { useProvider } from 'screens/Auth/SignupV2/useProvider';
import { useProductInstrumentation } from 'contexts/productInstrumentation';
import useFetch from 'hooks/useFetch';
import { useMutation } from '@tanstack/react-query';
import { request } from 'utils/api';
import { useUser } from 'contexts/user';
import { useAccount } from 'contexts/account';

export type IncompletePersonalDetails = {
  firstName: string;
  lastName: string;
  email: string;
  phoneCountryCode: string;
  phoneNo: string;
  countryCode: string;
  acceptedPrivacy: boolean;
  acceptedTerms: boolean;
};

type PersonalAndAccountDetailsProps = {
  onSubmit: () => Promise<void>;
};

import { FormikProps } from 'formik';
import { useCallback } from 'react';
import { ValidationError } from 'types/api';

export default function PersonalDetailsSSOStep(
  props: PersonalAndAccountDetailsProps
) {
  const { t, i18n } = useTranslation();
  const provider = useProvider();
  const { user, setUser } = useUser();
  const { account, getAccount } = useAccount();
  const platformName = provider.platformName;
  const { productInstrumentation } = useProductInstrumentation();
  const [error, setError] = useState<Error | null>(null);

  const errorFormatter = useCallback(
    (error: ValidationError, formikProps: FormikProps<any>) => {
      if (!formikProps) {
        return;
      }
      switch (error.code) {
        case 'INVALID_PHONE_NUMBER':
          formikProps.setFieldError(
            'phoneNo',
            t(
              'signup.errors.invalidPhoneNumber',
              'Please enter a valid phone number'
            )
          );
          break;
        default:
          console.error('Unhandled signup error', error);
      }
    },
    [t]
  );

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(
      t('signup.firstNameIsRequired', 'First Name is required')
    ),
    lastName: Yup.string().required(
      t('signup.lastNameIsRequired', 'Last Name is required')
    ),
    phoneCountryCode: Yup.string().required(
      t('signup.phoneCountryCodeIsRequired', 'Phone Country Code is required')
    ),
    phoneNo: Yup.string().required(
      t('signup.phoneNumberIsRequired', 'Phone Number is required')
    ),
    countryCode: Yup.string().required(
      t('signup.countryIsRequired', 'Country is required')
    ),
    acceptedPrivacy: Yup.boolean().oneOf([true]),
    acceptedTerms: Yup.boolean().oneOf([true]),
  });

  // combine personal and account details validation schemas

  const { data: allowedCountries } = useFetch<string[]>({
    path: '/1/signup/countries',
  });
  const sortCountries = (a: CountryOption, b: CountryOption) =>
    a.text.localeCompare(b.text);

  useEffect(() => {
    productInstrumentation?.trackJourneyStep({
      journey: 'sign-up',
      step: 'account-creation',
      stage: 'started',
      variant: '1.1.0',
      providerSlug: provider?.slug || 'unknown',
    });
  }, []);

  const finalizeAccount = useMutation({
    mutationFn: async (values: IncompletePersonalDetails) => {
      await request({
        path: '/1/accounts/self/finalize',
        method: 'POST',
        body: {
          languageCode: i18n.language,
          countryCode: values.countryCode,
          name: `${values.firstName} ${values.lastName}`,
        },
      });

      const response = await request({
        path: '/1/users/me',
        method: 'PATCH',
        body: {
          acceptanceDate: new Date(),
          defaultLangCode: i18n.language,
          contact: {
            ...user?.contact,
            firstName: values.firstName,
            lastName: values.lastName,
            phoneNo: values.phoneNo,
            phoneCountryCode: values.phoneCountryCode,
          },
        },
      });
      if (response.data) {
        setUser(response.data);
      }

      if (getAccount) {
        await getAccount();
      }
    },
    onSuccess() {
      props.onSubmit();
    },
  });

  if (!user || !account) {
    return null;
  }

  return (
    <Formik<IncompletePersonalDetails>
      initialValues={{
        firstName: user.contact?.firstName || '',
        lastName: user.contact?.lastName || '',
        email: user.email || '',
        countryCode: account?.billing?.countryCode || '',
        phoneCountryCode: user.contact?.phoneCountryCode || '',
        phoneNo: user.contact?.phoneNo || '',
        acceptedPrivacy: false,
        acceptedTerms: false,
      }}
      validationSchema={validationSchema}
      validateOnBlur
      validateOnChange
      onSubmit={async (values, formik) => {
        setError(null);
        finalizeAccount.mutate(values, {
          onError: (error: any) => {
            if (error.details) {
              error.details.forEach((detail: any) => {
                errorFormatter(detail, formik as any);
              });
            }
            setError(error);
          },
        });
      }}>
      {({ handleSubmit, isSubmitting }) => (
        <>
          <Form>
            <Form.Group widths={2}>
              <Form.Field>
                <InputField
                  required
                  label={t('formLabel.firstName', 'First Name')}
                  name="firstName"
                  type="text"
                />
              </Form.Field>
              <Form.Field>
                <InputField
                  required
                  label={t('formLabel.lastName', 'Last Name')}
                  name="lastName"
                  type="text"
                />
              </Form.Field>
            </Form.Group>
            <div
              className="required field"
              style={{ margin: 0, marginTop: '1em', marginBottom: 0 }}>
              <label>{t('formLabel.phoneNumber', 'Phone Number')}</label>
            </div>
            <Form.Group
              unstackable
              widths={'equal'}
              style={{ flexWrap: 'nowrap', marginBottom: '0.7em' }}>
              <PhoneCountry
                style={{ minWidth: '9rem' }}
                name="phoneCountryCode"
                whitelist={allowedCountries || undefined}
              />
              <PhoneNumber required name="phoneNo" />
            </Form.Group>
            {!account?.billing?.countryCode && (
              <Countries
                name="countryCode"
                required
                label={t('formLabel.country', 'Country')}
                comparator={sortCountries}
                whitelist={
                  allowedCountries?.map((c) => c.toLowerCase()) || undefined
                }
              />
            )}
            <Form.Field>
              <InputField
                disabled={!!user.email}
                name="email"
                required
                type={'text'}
                label={t('formLabel.email', 'Email')}
              />
            </Form.Field>
            <Divider hidden />
            <TermsAndPrivacy platformName={platformName} />
            <Divider hidden />
            {error && <ErrorMessage error={error} />}
            <Button
              as="button"
              type="submit"
              onClick={(e: any) => {
                productInstrumentation?.trackClick({
                  target: 'finalize-account',
                  journey: 'sign-up',
                  step: 'account-creation',
                  variant: '1.1.0',
                });
                return handleSubmit(e);
              }}
              disabled={isSubmitting}
              loading={isSubmitting}
              primary
              size="large"
              content={t('signup.completeAccount', 'Complete Account')}
              fluid
            />
          </Form>
        </>
      )}
    </Formik>
  );
}

export function TermsAndPrivacy({ platformName }: { platformName: string }) {
  const { t } = useTranslation();

  return (
    <>
      <CheckboxField
        name="acceptedTerms"
        label={
          <label>
            <span
              style={{
                display: 'block',
                lineHeight: '1.5em',
                transform: 'translateY(-2px)',
              }}>
              {t('signup.acceptTermsOfServices', 'I accept the')}{' '}
              <LegalLink type="tos">
                {t('signup.termsOfService', 'Terms of Service')}
              </LegalLink>
              . <RequiredIndicator />
            </span>
          </label>
        }
        hideErrorLabel
      />

      <CheckboxField
        name="acceptedPrivacy"
        label={
          <label style={{ maxWidth: '370px' }}>
            <span
              style={{
                display: 'block',
                lineHeight: '1.5em',
                transform: 'translateY(-2px)',
              }}>
              {t(
                'signup.acceptPrivacy',
                'I understand that {{platformName}} will process my personal data in accordance with the',
                {
                  platformName,
                }
              )}{' '}
              <LegalLink type="privacy">
                {t('signup.privacyStatement', 'Privacy Statement')}.{' '}
                <RequiredIndicator />
              </LegalLink>
            </span>
          </label>
        }
        hideErrorLabel
      />
    </>
  );
}
